// src/components/CreateSpace/Step2/components/PicturesSection.jsx
import React, { useEffect, useMemo } from "react";
import { ImageLoader, MessageBox } from "reusableComponents";
import styles from "./styles.module.scss";
import typographyStyles from "../../styles/_typography.module.scss";

/**
 * Determines error type based on message content
 * @param {string} message - Error message
 * @returns {'error'|'warning'|'info'}
 */
const getErrorTypeFromMessage = (message) => {
  if (!message || typeof message !== 'string') return 'error';
  
  const lowerMsg = message.toLowerCase();
  
  if (lowerMsg.includes('too large') || 
      lowerMsg.includes('failed') ||
      lowerMsg.includes('error') ||
      lowerMsg.includes('exceeds')) {
    return 'error';
  }
  
  if (lowerMsg.includes('warning') || 
      lowerMsg.includes('still') || 
      lowerMsg.includes('above')) {
    return 'warning';
  }
  
  return 'info';
};

const PicturesSection = ({
  formValues,
  fieldErrors,
  serverErrors,
  imageErrors,
  hasExtraPicturesAccess,
  compressingImages,
  onImageChange,
  onDescriptionChange,
  t
}) => {

  // Add this useEffect to monitor changes
  useEffect(() => {
    console.log("📸 PicturesSection - Image states:", {
      image1: formValues.image_1 ? (formValues.image_1 instanceof File ? 'File' : 'String') : 'null',
      image2: formValues.image_2 ? (formValues.image_2 instanceof File ? 'File' : 'String') : 'null',
      image3: formValues.image_3 ? (formValues.image_3 instanceof File ? 'File' : 'String') : 'null',
    });
  }, [formValues.image_1, formValues.image_2, formValues.image_3]);

  // Debug logging
  useEffect(() => {
    console.log("📸 PicturesSection - Props:", {
      serverErrors,
      imageErrors,
      fieldErrors,
      hasExtraPicturesAccess
    });
  }, [serverErrors, imageErrors, fieldErrors, hasExtraPicturesAccess]);

  const isImageCompressing = (imageIndex) => {
    return compressingImages[`image_${imageIndex}`] || false;
  };

  // Memoize error lookup to prevent recalculating on every render
  const getImageError = useMemo(() => {
    return (index) => {
      const imageKey = `image_${index}`;
      
      // Priority 1: imageErrors (compression-specific errors)
      if (imageErrors && imageErrors[imageKey]) {
        console.log(`📸 Found image error for ${imageKey}:`, imageErrors[imageKey]);
        
        return {
          message: imageErrors[imageKey],
          type: getErrorTypeFromMessage(imageErrors[imageKey])
        };
      }
      
      // Priority 2: fieldErrors (client-side validation)
      if (fieldErrors && fieldErrors[imageKey]) {
        console.log(`📸 Found field error for ${imageKey}:`, fieldErrors[imageKey]);
        
        return {
          message: fieldErrors[imageKey],
          type: 'error' // Validation errors are always errors
        };
      }
      
      // Priority 3: serverErrors (API validation)
      if (serverErrors && Array.isArray(serverErrors) && serverErrors.length > 0) {
        // Check various ways the error might be associated with this image
        const serverError = serverErrors.find(err => {
          // Direct field match
          if (err.field === imageKey) return true;
          if (err.code === imageKey) return true;
          
          // Check if error message contains the image key
          if (err.message && err.message.includes(imageKey)) return true;
          
          // Check if error message mentions this image number
          if (err.message && err.message.includes(`image ${index}`)) return true;
          
          return false;
        });
        
        if (serverError) {
          console.log(`📸 Found server error for ${imageKey}:`, serverError);
          
          return {
            message: serverError.message,
            type: serverError.type || getErrorTypeFromMessage(serverError.message)
          };
        }
      }
      
      return null;
    };
  }, [imageErrors, fieldErrors, serverErrors]);

  // Update renderImageLoader to use a key prop
  const renderImageLoader = (index, isRequired = false, label = null) => {
    const imageKey = `image_${index}`;
    const currentValue = formValues[imageKey];
    const isCompressing = isImageCompressing(index); // Call the function to get the value
    const error = getImageError(index);
    const hasError = !!error;
    
    console.log(`🖼️ Rendering ImageLoader ${index}, has value: ${!!currentValue}, compressing: ${isCompressing}`);
    
    return (
      <div
        key={`image-loader-${index}-${!!currentValue}-${isCompressing}`}
        className={`${styles.imageLoaderContainer} ${isCompressing ? styles.compressing : ''} ${hasError ? styles.hasError : ''}`}
        data-testid={`image-loader-${index}`}
        data-error={hasError ? "true" : "false"}
      >
        <ImageLoader
          key={`image-${index}-${!!currentValue}`}
          errorMessage={error?.message || null}
          errorType={error?.type || null}
          required={isRequired}
          index={index}
          formValues={formValues}
          onImageChange={onImageChange}
          onDescriptionChange={onDescriptionChange}
          compressing={isCompressing}
          label={label || `${t("provide.step2.pictures.picture")} ${index}`}
          aria-label={`Upload image ${index}`}
          aria-invalid={hasError}
          aria-describedby={hasError ? `image-error-${index}` : undefined}
        />
        {isCompressing && (
          <div className={styles.compressionOverlay} role="status" aria-live="polite">
            <div className={styles.compressionSpinner} aria-hidden="true" />
            <span className={styles.compressionText}>
              {t('common.compressing') || 'Compressing...'}
            </span>
          </div>
        )}
        {hasError && error?.type === 'warning' && (
          <div id={`image-error-${index}`} className={styles.warningMessage} role="alert">
            {error.message}
          </div>
        )}
      </div>
    );
  };

  return (
    <section className={styles.picturesSection} aria-labelledby="pictures-title">
      <h4 id="pictures-title" className={typographyStyles.subtitle}>
        {t("provide.step2.pictures.title")}
      </h4>

      {/* Image size info message */}
      <MessageBox
        type="info"
        message={t("provide.step2.pictures.sizes", {
          width: 414,
          height: 232
        })}
        aria-label="Image size requirements"
      />

      <div className={styles.groupedItems}>
        {/* Cover Image (Required) */}
        {renderImageLoader(
          1,
          true,
          `${t("provide.step2.pictures.cover")} *`
        )}

        {/* Regular Images 2 & 3 */}
        {[2, 3].map(index => renderImageLoader(index))}
      </div>

      {/* Extra Images Section */}
      {!hasExtraPicturesAccess && (
        <div className={styles.extraImagesSection}>
          <div className={styles.sectionHeader}>
            <h5 className={styles.sectionTitle}>
              {t("provide.step2.picturesPaying.title")}
            </h5>
          </div>
          <MessageBox
            type="info"
            message={t("provide.step2.picturesPaying.description")}
          />
        </div>
      )}

      {/* Extra Images */}
      <div className={styles.groupedItems}>
        {[4, 5, 6].map(index => renderImageLoader(index))}
      </div>
    </section>
  );
};

// Use memo to prevent unnecessary re-renders
export default React.memo(PicturesSection);